// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { makeStyles, tokens, Title1, Body1 } from "@fluentui/react-components";
import { getRuntimeEnvValue } from "../../../utils/runtimeEnv";
import { useBillingPortalUrl } from "./useBillingPortalUrl";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXL,
    background: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: "460px",
    width: "100%",
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow8,
    padding: tokens.spacingVerticalXXL,
    display: "grid",
    gap: tokens.spacingVerticalL,
    justifyItems: "start",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  accent: {
    height: "4px",
    width: "72px",
    background: tokens.colorBrandForeground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  body: {
    color: tokens.colorNeutralForeground2,
  },
  email: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorBrandForeground1,
  },
  cta: {
    display: "inline-block",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: tokens.borderRadiusMedium,
    fontWeight: tokens.fontWeightSemibold,
    textDecoration: "none",
    ":hover": {
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  portalLink: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
    textDecoration: "none",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    font: "inherit",
    ":hover": {
      textDecoration: "underline",
    },
  },
  marketingLink: {
    color: tokens.colorNeutralForeground3,
    textDecoration: "none",
    ":hover": {
      textDecoration: "underline",
    },
  },
});

export function SubscriptionGateScreen() {
  const styles = useStyles();
  const marketingUrl = getRuntimeEnvValue("VITE_MARKETING_BASE_URL") ?? "";
  const { openPortal, isResolving } = useBillingPortalUrl();
  const hostname =
    marketingUrl.length > 0
      ? new URL(
          marketingUrl.includes("://") ? marketingUrl : `https://${marketingUrl}`,
        ).hostname
      : "riddledevs.com";
  const pricingUrl =
    marketingUrl.length > 0 ? `${marketingUrl.replace(/\/$/, "")}/pricing` : "";

  return (
    <main className={styles.root}>
      <section className={styles.card} aria-label="Subscription required">
        <div className={styles.accent} />
        <Title1>Your subscription is not active</Title1>
        <Body1 className={styles.body}>
          A paid Riddle subscription is required to continue. To verify your plan
          or re-subscribe, visit us at{" "}
          <a
            className={styles.marketingLink}
            href={marketingUrl.length > 0 ? marketingUrl : `https://${hostname}`}
            target="_blank"
            rel="noreferrer"
          >
            {hostname}
          </a>
          .
        </Body1>
        {pricingUrl.length > 0 && (
          <a
            className={styles.cta}
            href={pricingUrl}
            target="_blank"
            rel="noreferrer"
          >
            View plans &amp; subscribe
          </a>
        )}
        <button
          className={styles.portalLink}
          type="button"
          disabled={isResolving}
          onClick={() => void openPortal()}
        >
          {isResolving ? "Opening your subscription…" : "Manage your subscription in Stripe →"}
        </button>
        <Body1 className={styles.body}>
          If you believe your payment was processed, open the Stripe portal to
          check your subscription status, invoices, and any payment or
          cancellation issues.
        </Body1>
        <Body1 className={styles.body}>
          Already subscribed or need help? Email{" "}
          <span className={styles.email}>billing@riddledevs.com</span>.
        </Body1>
      </section>
    </main>
  );
}
